import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  DollarSign,
  Edit3,
  Layers,
  Loader2,
  MapPin,
  Plus,
  RefreshCw,
  Search,
  Users,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import Modal from '../../components/Modal';
import {
  btnPrimary,
  btnSecondary,
  inputCls,
  labelCls,
  pageSubtitleCls,
  pageTitleCls,
  selectCls,
  tableWrapCls,
  tdCls,
  thCls,
  theadCls,
  trCls,
} from '../../components/ui';

interface CampoDepartamento {
  id: string;
  codigo_vfp: string;
  key_vfp: string | null;
  nombre: string;
  tarifa_base: number | null;
  activo: boolean;
}

interface CampoLabor {
  id: string;
  departamento_id: string | null;
  codigo_vfp: string;
  key_vfp: string | null;
  parent_vfp: string | null;
  nombre: string;
  modalidad_default: 'hora' | 'fijo' | 'tarea' | 'produccion';
  unidad_default: string;
  tarifa_base: number | null;
  requiere_lote: boolean;
  requiere_bloque: boolean;
  requiere_estadio: boolean;
  requiere_gps: boolean;
  requiere_produccion: boolean;
  requiere_maquinaria: boolean;
  activo: boolean;
}

type ModalState =
  | { tipo: 'depto'; initial?: CampoDepartamento }
  | { tipo: 'labor'; initial?: CampoLabor }
  | null;

type LaborModalidad = CampoLabor['modalidad_default'];

const MODALIDAD_LABEL: Record<CampoLabor['modalidad_default'], string> = {
  hora: 'Hora',
  fijo: 'Fijo',
  tarea: 'Tarea',
  produccion: 'Produccion',
};

function fmtMoney(v?: number | null) {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n) || n === 0) return '-';
  return 'CRC ' + n.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Kpi({
  label,
  value,
  icon,
  tone = 'var(--emp-accent-txt)',
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  tone?: string;
}) {
  return (
    <div className="rounded-lg border px-4 py-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
      <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider" style={{ color: tone }}>
        {icon}
        {label}
      </div>
      <div className="font-mono text-2xl font-bold" style={{ color: 'var(--ink)' }}>{value}</div>
    </div>
  );
}

function Badge({ children, active = true }: { children: React.ReactNode; active?: boolean }) {
  return (
    <span
      className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold"
      style={{
        background: active ? 'var(--emp-accent-bg)' : 'var(--surface-overlay)',
        border: `1px solid ${active ? 'var(--emp-accent)' : 'var(--line)'}`,
        color: active ? 'var(--emp-accent-txt)' : 'var(--ink-faint)',
      }}
    >
      {children}
    </span>
  );
}

function CheckField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      className="flex cursor-pointer items-center justify-between gap-3 rounded border px-3 py-2 text-xs"
      style={{
        background: 'var(--surface-overlay)',
        borderColor: checked ? 'var(--emp-accent)' : 'var(--line)',
      }}
    >
      <span style={{ color: checked ? 'var(--emp-accent-txt)' : 'var(--ink-muted)' }}>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-current"
        style={{ color: 'var(--emp-accent)' }}
      />
    </label>
  );
}

function DepartamentoForm({
  empresaId,
  initial,
  onClose,
  onSaved,
  onError,
}: {
  empresaId: number;
  initial?: CampoDepartamento;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const [codigo, setCodigo] = useState(initial?.codigo_vfp ?? '');
  const [keyVfp, setKeyVfp] = useState(initial?.key_vfp ?? '');
  const [nombre, setNombre] = useState(initial?.nombre ?? '');
  const [tarifa, setTarifa] = useState(String(initial?.tarifa_base ?? 0));
  const [saving, setSaving] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    const cod = codigo.trim();
    const nom = nombre.trim();
    if (!cod || !nom) {
      onError('Codigo y nombre son requeridos.');
      return;
    }
    setSaving(true);
    const payload = {
      empresa_id: empresaId,
      codigo_vfp: cod,
      key_vfp: keyVfp.trim() || null,
      nombre: nom,
      tarifa_base: Number(tarifa || 0),
      activo: true,
    };
    const req = initial
      ? supabase.from('campo_labor_departamentos').update(payload).eq('id', initial.id).eq('empresa_id', empresaId)
      : supabase.from('campo_labor_departamentos').insert(payload);
    const { error } = await req;
    setSaving(false);
    if (error) {
      onError(error.message);
      return;
    }
    onSaved();
  }

  return (
    <form onSubmit={guardar} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <label className={labelCls}>Codigo VFP</label>
        <input className={inputCls} value={codigo} onChange={(e) => setCodigo(e.target.value)} />
      </div>
      <div>
        <label className={labelCls}>Key VFP</label>
        <input className={inputCls} value={keyVfp} onChange={(e) => setKeyVfp(e.target.value)} placeholder="Ej: 126_" />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls}>Departamento</label>
        <input className={inputCls} value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </div>
      <div>
        <label className={labelCls}>Tarifa base</label>
        <input className={inputCls} type="number" step="0.01" value={tarifa} onChange={(e) => setTarifa(e.target.value)} />
      </div>
      <div className="flex items-end justify-end gap-2 sm:col-span-2">
        <button type="button" className={btnSecondary} onClick={onClose} disabled={saving}>Cancelar</button>
        <button type="submit" className={btnPrimary} disabled={saving}>
          {saving ? 'Guardando...' : initial ? 'Actualizar' : 'Crear departamento'}
        </button>
      </div>
    </form>
  );
}

function LaborForm({
  empresaId,
  deptos,
  selectedDeptoId,
  initial,
  onClose,
  onSaved,
  onError,
}: {
  empresaId: number;
  deptos: CampoDepartamento[];
  selectedDeptoId: string;
  initial?: CampoLabor;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const defaultDepto = initial?.departamento_id || selectedDeptoId || deptos[0]?.id || '';
  const [departamentoId, setDepartamentoId] = useState(defaultDepto);
  const [codigo, setCodigo] = useState(initial?.codigo_vfp ?? '');
  const [keyVfp, setKeyVfp] = useState(initial?.key_vfp ?? '');
  const [nombre, setNombre] = useState(initial?.nombre ?? '');
  const [modalidad, setModalidad] = useState<LaborModalidad>(initial?.modalidad_default ?? 'hora');
  const [unidad, setUnidad] = useState(initial?.unidad_default ?? 'hora');
  const [tarifa, setTarifa] = useState(String(initial?.tarifa_base ?? 0));
  const [requiereLote, setRequiereLote] = useState(initial?.requiere_lote ?? true);
  const [requiereBloque, setRequiereBloque] = useState(initial?.requiere_bloque ?? false);
  const [requiereEstadio, setRequiereEstadio] = useState(initial?.requiere_estadio ?? false);
  const [requiereGps, setRequiereGps] = useState(initial?.requiere_gps ?? true);
  const [requiereProduccion, setRequiereProduccion] = useState(initial?.requiere_produccion ?? false);
  const [requiereMaquinaria, setRequiereMaquinaria] = useState(initial?.requiere_maquinaria ?? false);
  const [saving, setSaving] = useState(false);

  const deptoActual = deptos.find((d) => d.id === departamentoId);

  useEffect(() => {
    if (initial) return;
    if (modalidad === 'produccion' && unidad === 'hora') setUnidad('unidad');
    if (modalidad === 'fijo' && unidad === 'hora') setUnidad('jornal');
  }, [modalidad, initial, unidad]);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    const cod = codigo.trim();
    const nom = nombre.trim();
    if (!departamentoId || !cod || !nom) {
      onError('Departamento, codigo y labor son requeridos.');
      return;
    }
    setSaving(true);
    const payload = {
      empresa_id: empresaId,
      departamento_id: departamentoId,
      codigo_vfp: cod,
      key_vfp: keyVfp.trim() || null,
      parent_vfp: deptoActual?.key_vfp || initial?.parent_vfp || null,
      nombre: nom,
      modalidad_default: modalidad,
      unidad_default: unidad.trim() || 'unidad',
      tarifa_base: Number(tarifa || 0),
      requiere_lote: requiereLote,
      requiere_bloque: requiereBloque,
      requiere_estadio: requiereEstadio,
      requiere_gps: requiereGps,
      requiere_produccion: requiereProduccion,
      requiere_maquinaria: requiereMaquinaria,
      activo: true,
    };
    const req = initial
      ? supabase.from('campo_labores').update(payload).eq('id', initial.id).eq('empresa_id', empresaId)
      : supabase.from('campo_labores').insert(payload);
    const { data, error } = await req.select('id').single();

    if (!error && Number(payload.tarifa_base) > 0) {
      const laborId = initial?.id || data?.id;
      if (laborId) {
        await supabase
          .from('campo_labor_tarifas')
          .update({ activo: false, vigente_hasta: new Date().toISOString().slice(0, 10) })
          .eq('empresa_id', empresaId)
          .eq('labor_id', laborId)
          .eq('activo', true)
          .is('vigente_hasta', null);
        await supabase.from('campo_labor_tarifas').insert({
          empresa_id: empresaId,
          labor_id: laborId,
          modalidad,
          unidad: payload.unidad_default,
          monto: payload.tarifa_base,
          vigente_desde: new Date().toISOString().slice(0, 10),
          activo: true,
        });
      }
    }

    setSaving(false);
    if (error) {
      onError(error.message);
      return;
    }
    onSaved();
  }

  return (
    <form onSubmit={guardar} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <label className={labelCls}>Departamento</label>
        <select className={selectCls} value={departamentoId} onChange={(e) => setDepartamentoId(e.target.value)}>
          <option value="">Seleccione</option>
          {deptos.map((d) => (
            <option key={d.id} value={d.id}>{d.codigo_vfp} - {d.nombre}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls}>Codigo VFP</label>
        <input className={inputCls} value={codigo} onChange={(e) => setCodigo(e.target.value)} />
      </div>
      <div>
        <label className={labelCls}>Key VFP</label>
        <input className={inputCls} value={keyVfp} onChange={(e) => setKeyVfp(e.target.value)} />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls}>Labor</label>
        <input className={inputCls} value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </div>
      <div>
        <label className={labelCls}>Modalidad</label>
        <select className={selectCls} value={modalidad} onChange={(e) => setModalidad(e.target.value as LaborModalidad)}>
          <option value="hora">Hora</option>
          <option value="fijo">Fijo</option>
          <option value="tarea">Tarea</option>
          <option value="produccion">Produccion</option>
        </select>
      </div>
      <div>
        <label className={labelCls}>Unidad</label>
        <input className={inputCls} value={unidad} onChange={(e) => setUnidad(e.target.value)} placeholder="hora, jornal, unidad, ha..." />
      </div>
      <div>
        <label className={labelCls}>Tarifa base</label>
        <input className={inputCls} type="number" step="0.01" value={tarifa} onChange={(e) => setTarifa(e.target.value)} />
      </div>
      <div className="grid grid-cols-1 gap-2 sm:col-span-2 sm:grid-cols-3">
        <CheckField label="Requiere lote" checked={requiereLote} onChange={setRequiereLote} />
        <CheckField label="Requiere bloque" checked={requiereBloque} onChange={setRequiereBloque} />
        <CheckField label="Requiere estadio" checked={requiereEstadio} onChange={setRequiereEstadio} />
        <CheckField label="Requiere GPS" checked={requiereGps} onChange={setRequiereGps} />
        <CheckField label="Requiere produccion" checked={requiereProduccion} onChange={setRequiereProduccion} />
        <CheckField label="Requiere maquinaria" checked={requiereMaquinaria} onChange={setRequiereMaquinaria} />
      </div>
      <div className="flex items-end justify-end gap-2 sm:col-span-2">
        <button type="button" className={btnSecondary} onClick={onClose} disabled={saving}>Cancelar</button>
        <button type="submit" className={btnPrimary} disabled={saving}>
          {saving ? 'Guardando...' : initial ? 'Actualizar' : 'Crear labor'}
        </button>
      </div>
    </form>
  );
}

export function LaboresCampoPage() {
  const empresaId = useEmpresaId();
  const [deptos, setDeptos] = useState<CampoDepartamento[]>([]);
  const [labores, setLabores] = useState<CampoLabor[]>([]);
  const [deptoId, setDeptoId] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState<ModalState>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');

    const [{ data: d, error: eDeptos }, { data: l, error: eLabores }] = await Promise.all([
      supabase
        .from('campo_labor_departamentos')
        .select('id, codigo_vfp, key_vfp, nombre, tarifa_base, activo')
        .eq('empresa_id', empresaId)
        .eq('activo', true)
        .order('codigo_vfp'),
      supabase
        .from('campo_labores')
        .select('id, departamento_id, codigo_vfp, key_vfp, parent_vfp, nombre, modalidad_default, unidad_default, tarifa_base, requiere_lote, requiere_bloque, requiere_estadio, requiere_gps, requiere_produccion, requiere_maquinaria, activo')
        .eq('empresa_id', empresaId)
        .eq('activo', true)
        .order('codigo_vfp'),
    ]);

    if (eDeptos || eLabores) {
      setError(eDeptos?.message || eLabores?.message || 'No se pudo cargar el catalogo de labores.');
      setDeptos([]);
      setLabores([]);
    } else {
      setDeptos((d ?? []) as CampoDepartamento[]);
      setLabores((l ?? []) as CampoLabor[]);
    }
    setLoading(false);
  }, [empresaId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const deptoMap = useMemo(() => {
    const m = new Map<string, CampoDepartamento>();
    for (const d of deptos) m.set(d.id, d);
    return m;
  }, [deptos]);

  const filtradas = useMemo(() => {
    const term = q.trim().toLowerCase();
    return labores.filter((l) => {
      if (deptoId && l.departamento_id !== deptoId) return false;
      if (!term) return true;
      const depto = l.departamento_id ? deptoMap.get(l.departamento_id)?.nombre ?? '' : '';
      return (
        l.codigo_vfp.toLowerCase().includes(term) ||
        l.nombre.toLowerCase().includes(term) ||
        depto.toLowerCase().includes(term)
      );
    });
  }, [labores, deptoId, q, deptoMap]);

  const stats = useMemo(() => {
    const conTarifa = labores.filter((l) => Number(l.tarifa_base ?? 0) > 0).length;
    const conGps = labores.filter((l) => l.requiere_gps).length;
    const conProduccion = labores.filter((l) => l.requiere_produccion).length;
    const conMaquinaria = labores.filter((l) => l.requiere_maquinaria).length;
    return { conTarifa, conGps, conProduccion, conMaquinaria };
  }, [labores]);

  async function onSavedModal() {
    setModal(null);
    await cargar();
  }

  const deptoSeleccionado = useMemo(
    () => deptos.find((d) => d.id === deptoId),
    [deptos, deptoId],
  );

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div
        className="shrink-0 px-5 py-4"
        style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--line)' }}
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className={pageTitleCls}>Labores de Campo</h1>
            <p className={pageSubtitleCls}>Catalogo VFP para planillas agricolas</p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div>
              <label className={labelCls}>Departamento</label>
              <select
                className="w-full rounded border border-line bg-surface-overlay px-3 py-2 text-sm text-ink sm:w-64"
                value={deptoId}
                onChange={(e) => setDeptoId(e.target.value)}
              >
                <option value="">Todos</option>
                {deptos.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.codigo_vfp} - {d.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Buscar</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-faint)' }} />
                <input
                  className={`${inputCls} pl-8 sm:w-72`}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Codigo, labor o departamento"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={cargar}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded border px-3 py-2 text-xs font-semibold"
              style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)', background: 'var(--surface-overlay)' }}
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Recargar
            </button>
            <button
              type="button"
              className={`${btnSecondary} inline-flex items-center justify-center gap-2`}
              onClick={() => setModal({ tipo: 'depto' })}
            >
              <Plus size={14} />
              Depto
            </button>
            <button
              type="button"
              className={`${btnPrimary} inline-flex items-center justify-center gap-2`}
              onClick={() => setModal({ tipo: 'labor' })}
              disabled={deptos.length === 0}
            >
              <Plus size={14} />
              Labor
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {error && (
          <div
            className="mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm"
            style={{ background: 'var(--tone-warn-bg)', borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-ink)' }}
          >
            <AlertTriangle size={17} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
          <Kpi label="Deptos" value={deptos.length} icon={<Layers size={14} />} />
          <Kpi label="Labores" value={labores.length} icon={<CheckCircle2 size={14} />} />
          <Kpi label="Con tarifa" value={stats.conTarifa} icon={<DollarSign size={14} />} tone="var(--tone-success-text)" />
          <Kpi label="GPS" value={stats.conGps} icon={<MapPin size={14} />} tone="var(--tone-info-text)" />
          <Kpi label="Produccion" value={stats.conProduccion} icon={<Users size={14} />} tone="var(--tone-purple-text)" />
          <Kpi label="Maquinaria" value={stats.conMaquinaria} icon={<Clock3 size={14} />} tone="var(--tone-warn-text)" />
        </div>

        {deptoSeleccionado && (
          <div
            className="mb-4 flex flex-col gap-3 rounded-lg border px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}
          >
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--ink-faint)' }}>
                Departamento seleccionado
              </div>
              <div className="truncate text-sm font-semibold" style={{ color: 'var(--ink)' }}>
                {deptoSeleccionado.codigo_vfp} - {deptoSeleccionado.nombre}
              </div>
            </div>
            <button
              type="button"
              className={`${btnSecondary} inline-flex items-center justify-center gap-2`}
              onClick={() => setModal({ tipo: 'depto', initial: deptoSeleccionado })}
            >
              <Edit3 size={13} />
              Editar departamento
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm" style={{ color: 'var(--ink-faint)' }}>
            <Loader2 size={18} className="animate-spin" />
            Cargando labores...
          </div>
        ) : labores.length === 0 ? (
          <div
            className="rounded-lg border border-dashed px-5 py-12 text-center"
            style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }}
          >
            No hay labores cargadas para esta empresa.
          </div>
        ) : (
          <div className={tableWrapCls}>
            <table className="w-full border-collapse text-xs">
              <thead className={theadCls}>
                <tr>
                  <th className={thCls}>Codigo</th>
                  <th className={thCls}>Labor</th>
                  <th className={thCls}>Departamento</th>
                  <th className={thCls}>Modalidad</th>
                  <th className={thCls}>Unidad</th>
                  <th className={`${thCls} text-right`}>Tarifa base</th>
                  <th className={thCls}>Controles</th>
                  <th className={`${thCls} text-right`}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((l) => {
                  const d = l.departamento_id ? deptoMap.get(l.departamento_id) : undefined;
                  return (
                    <tr key={l.id} className={trCls}>
                      <td className={`${tdCls} font-mono font-bold`} style={{ color: 'var(--emp-accent-txt)' }}>
                        {l.codigo_vfp}
                      </td>
                      <td className={`${tdCls} font-semibold`} style={{ color: 'var(--ink)' }}>
                        {l.nombre}
                      </td>
                      <td className={tdCls}>
                        {d ? `${d.codigo_vfp} - ${d.nombre}` : '-'}
                      </td>
                      <td className={tdCls}>
                        <Badge>{MODALIDAD_LABEL[l.modalidad_default]}</Badge>
                      </td>
                      <td className={tdCls}>{l.unidad_default}</td>
                      <td className={`${tdCls} text-right font-mono`}>{fmtMoney(l.tarifa_base)}</td>
                      <td className={tdCls}>
                        <div className="flex flex-wrap gap-1">
                          <Badge active={l.requiere_lote}>Lote</Badge>
                          <Badge active={l.requiere_bloque}>Bloque</Badge>
                          <Badge active={l.requiere_estadio}>Estadio</Badge>
                          <Badge active={l.requiere_gps}>GPS</Badge>
                          <Badge active={l.requiere_produccion}>Prod.</Badge>
                          <Badge active={l.requiere_maquinaria}>Maq.</Badge>
                        </div>
                      </td>
                      <td className={`${tdCls} text-right`}>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center gap-1 rounded border px-2 py-1 text-[11px] font-semibold"
                          style={{
                            background: 'var(--surface-overlay)',
                            borderColor: 'var(--line)',
                            color: 'var(--ink-muted)',
                          }}
                          onClick={() => setModal({ tipo: 'labor', initial: l })}
                        >
                          <Edit3 size={12} />
                          Editar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && labores.length > 0 && filtradas.length === 0 && (
          <div className="mt-4 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
            Sin coincidencias para el filtro actual.
          </div>
        )}
      </div>

      {modal?.tipo === 'depto' && (
        <Modal
          title={modal.initial ? 'Editar departamento' : 'Nuevo departamento'}
          onClose={() => setModal(null)}
          size="lg"
        >
          <DepartamentoForm
            empresaId={empresaId}
            initial={modal.initial}
            onClose={() => setModal(null)}
            onSaved={onSavedModal}
            onError={setError}
          />
        </Modal>
      )}

      {modal?.tipo === 'labor' && (
        <Modal
          title={modal.initial ? 'Editar labor' : 'Nueva labor'}
          onClose={() => setModal(null)}
          size="xl"
        >
          <LaborForm
            empresaId={empresaId}
            deptos={deptos}
            selectedDeptoId={deptoId}
            initial={modal.initial}
            onClose={() => setModal(null)}
            onSaved={onSavedModal}
            onError={setError}
          />
        </Modal>
      )}
    </div>
  );
}

export default LaboresCampoPage;
